import {
  createWorkspaceInvitation,
  getWorkspaceSettings,
  listWorkspaceAgents,
  listWorkspaceInvitations,
  listWorkspaceMembers,
  removeWorkspaceMember,
  resendWorkspaceInvitation,
  revokeWorkspaceInvitation,
  updateWorkspaceSettings,
  type CoworkWorkspaceInvitation,
  type CoworkWorkspaceMember,
} from "@/cowork/api"
import { COWORK_DEFAULT_AGENT_KEY } from "@/components/settings-v2/cowork-agents"
import { visibleWorkspaceDefaultAgentID, workspaceDefaultAgentOptions } from "@/cowork/default-agent"
import { CoworkWorkspaceProfileSection, CoworkWorkspaceUrlRow } from "@/components/cowork-workspace-identity"
import { SettingsListV2 } from "@/components/settings-v2/parts/list"
import { SettingsRowV2 } from "@/components/settings-v2/parts/row"
import { useCoworkAuth } from "@/cowork/auth"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { useLanguage } from "@/context/language"
import { formatDateTime } from "@/utils/date"
import { SettingsReturnLink } from "@/components/settings-return-link"
import { createEffect, createMemo, createResource, For, Show } from "solid-js"
import { createStore } from "solid-js/store"

export function CoworkWorkspaceSettingsPage() {
  const language = useLanguage()
  const auth = useCoworkAuth()
  const context = useCoworkWorkspace()
  const [members, memberActions] = createResource(listWorkspaceMembers)
  const [invitations, invitationActions] = createResource(listWorkspaceInvitations)
  const [settings, settingsActions] = createResource(getWorkspaceSettings)
  const [agents] = createResource(listWorkspaceAgents)
  const agentOptions = createMemo(() => workspaceDefaultAgentOptions(agents() ?? [], [
    { id: "custom-report", name: language.t("cowork.agents.template.customReport"), templateID: "custom-report" },
    { id: "accounting", name: language.t("cowork.agents.template.accounting"), templateID: "accounting" },
  ]))
  const [state, setState] = createStore({
    identifier: "",
    role: "member" as "viewer" | "member" | "admin",
    adding: false,
    removing: "",
    invitationBusy: "",
    invitationNotice: "",
    defaultAgentID: "accounting",
    workspaceName: "",
    savingName: false,
    savingAgent: false,
    error: "",
  })
  createEffect(() => {
    const value = settings()?.default_agent_id
    const workspaceName = settings()?.workspace_name
    if (settings() && !agents.loading) setState("defaultAgentID", visibleWorkspaceDefaultAgentID(value, agents() ?? [], agentOptions()))
    if (workspaceName) setState("workspaceName", workspaceName)
  })

  const invite = async (event: SubmitEvent) => {
    event.preventDefault()
    const identifier = state.identifier.trim()
    if (!identifier || state.adding || seatsFull()) return
    setState({ adding: true, error: "", invitationNotice: "" })
    try {
      const invitation = await createWorkspaceInvitation(identifier, state.role)
      setState({ identifier: "", invitationNotice: invitation.email })
      await Promise.all([invitationActions.refetch(), settingsActions.refetch()])
    } catch (error) {
      setState("error", invitationErrorMessage(error))
    } finally {
      setState("adding", false)
    }
  }

  const remove = async (member: CoworkWorkspaceMember) => {
    if (state.removing) return
    setState({ removing: member.user_id, error: "" })
    try {
      await removeWorkspaceMember(member.user_id)
      await Promise.all([memberActions.refetch(), settingsActions.refetch()])
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("removing", "")
    }
  }

  const resendInvitation = async (invitation: CoworkWorkspaceInvitation) => {
    if (state.invitationBusy) return
    setState({ invitationBusy: invitation.id, error: "", invitationNotice: "" })
    try {
      await resendWorkspaceInvitation(invitation.id)
      setState("invitationNotice", invitation.email)
      await invitationActions.refetch()
    } catch (error) {
      setState("error", invitationErrorMessage(error))
    } finally {
      setState("invitationBusy", "")
    }
  }

  const revokeInvitation = async (invitation: CoworkWorkspaceInvitation) => {
    if (state.invitationBusy) return
    setState({ invitationBusy: invitation.id, error: "", invitationNotice: "" })
    try {
      await revokeWorkspaceInvitation(invitation.id)
      await Promise.all([invitationActions.refetch(), settingsActions.refetch()])
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("invitationBusy", "")
    }
  }

  const setDefaultAgent = async (agentID: string) => {
    const previous = state.defaultAgentID
    setState({ defaultAgentID: agentID, savingAgent: true, error: "" })
    try {
      await updateWorkspaceSettings({ default_agent_id: agentID })
      localStorage.setItem(COWORK_DEFAULT_AGENT_KEY, agentID)
      window.dispatchEvent(new CustomEvent("cowork:default-agent-changed", { detail: { agentID } }))
      void context.refreshWorkspaceSettings().catch(() => undefined)
      void settingsActions.refetch().catch(() => undefined)
    } catch (error) {
      setState({ defaultAgentID: previous, error: error instanceof Error ? error.message : String(error) })
    } finally {
      setState("savingAgent", false)
    }
  }

  const saveWorkspaceName = async (event: SubmitEvent) => {
    event.preventDefault()
    const workspaceName = state.workspaceName.trim()
    if (!workspaceName || state.savingName) return
    setState({ savingName: true, error: "" })
    try {
      await updateWorkspaceSettings({ workspace_name: workspaceName })
      await context.refreshWorkspaceSettings()
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("savingName", false)
    }
  }

  const invitationErrorMessage = (error: unknown) => {
    const message = error instanceof Error ? error.message : String(error)
    if (message === "email_delivery_failed") return language.t("cowork.enterprise.settings.invitations.deliveryFailed")
    if (message === "seat_limit_reached") {
      return language.t("cowork.enterprise.settings.invitations.seatLimitReached", {
        limit: String(settings()?.seat_limit ?? 5),
      })
    }
    return message
  }

  const seatsFull = () => {
    const value = settings()
    if (!value?.seat_limit) return false
    return (value.seat_used ?? 0) >= value.seat_limit
  }

  const isCurrentUser = (member: CoworkWorkspaceMember) =>
    !!auth.state.accountEmail && member.email?.toLowerCase() === auth.state.accountEmail.toLowerCase()

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1100px]">
        <SettingsReturnLink always />
        <header>
          <h1 class="text-[30px] font-semibold tracking-[-0.025em]">
            {language.t("cowork.enterprise.settings.workspace")}
          </h1>
          <p class="mt-1 text-[13px] text-v2-text-text-muted">
            {language.t("cowork.enterprise.settings.workspaceDescription")}
          </p>
        </header>

        <div class="mt-7 space-y-7">
          <section>
            <h2 class="settings-v2-section-title">{language.t("cowork.enterprise.settings.workspaceIdentity")}</h2>
            <SettingsListV2>
              <SettingsRowV2
                title={language.t("cowork.enterprise.settings.workspaceName")}
                description={language.t("cowork.enterprise.settings.workspaceNameDescription")}
              >
                <form class="flex w-full justify-end gap-2" onSubmit={saveWorkspaceName}>
                  <input
                    class="h-9 min-w-0 flex-1 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-v2-text-text-base outline-none sm:w-64 sm:flex-none"
                    value={state.workspaceName}
                    aria-label={language.t("cowork.enterprise.settings.workspaceName")}
                    disabled={state.savingName || settings.loading}
                    onInput={(event) => setState("workspaceName", event.currentTarget.value)}
                  />
                  <button
                    type="submit"
                    class="h-9 rounded-md bg-v2-background-bg-contrast px-3 text-[12px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                    disabled={!state.workspaceName.trim() || state.savingName}
                  >
                    {state.savingName
                      ? language.t("cowork.enterprise.settings.saving")
                      : language.t("cowork.enterprise.settings.save")}
                  </button>
                </form>
              </SettingsRowV2>
              <CoworkWorkspaceUrlRow
                slug={settings()?.slug}
                loading={settings.loading}
                onError={(message) => setState("error", message)}
              />
            </SettingsListV2>
          </section>

          <CoworkWorkspaceProfileSection
            onError={(message) => setState("error", message)}
            onSaved={() => void memberActions.refetch()}
          />

          <section>
            <h2 class="settings-v2-section-title">{language.t("cowork.enterprise.settings.defaultAgent")}</h2>
            <SettingsListV2>
              <SettingsRowV2
                title={language.t("cowork.settings.agents.default.title")}
                description={language.t("cowork.enterprise.settings.defaultAgentDescription")}
              >
                <select
                  class="h-9 min-w-[220px] rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-13-regular text-v2-text-text-base outline-none"
                  value={state.defaultAgentID}
                  disabled={state.savingAgent || settings.loading || agents.loading}
                  onChange={(event) => void setDefaultAgent(event.currentTarget.value)}
                >
                  <For each={agentOptions()}>
                    {(agent) => <option value={agent.id}>{agent.name}</option>}
                  </For>
                </select>
              </SettingsRowV2>
            </SettingsListV2>
          </section>

          <section>
            <h2 class="settings-v2-section-title">{language.t("cowork.enterprise.settings.members")}</h2>
            <SettingsListV2>
              <SettingsRowV2
                title={language.t("cowork.enterprise.settings.inviteMember")}
                description={language.t("cowork.enterprise.settings.inviteDescription")}
              >
                <form class="flex w-full flex-wrap justify-end gap-2" onSubmit={invite}>
                  <input
                    class="h-9 min-w-0 flex-1 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-v2-text-text-base outline-none sm:w-64 sm:flex-none"
                    type="email"
                    value={state.identifier}
                    placeholder={language.t("cowork.login.emailPlaceholder")}
                    aria-label={language.t("cowork.enterprise.settings.memberIdentifier")}
                    disabled={state.adding || seatsFull()}
                    onInput={(event) => setState("identifier", event.currentTarget.value)}
                  />
                  <select
                    class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-v2-text-text-base outline-none"
                    value={state.role}
                    disabled={state.adding || seatsFull()}
                    aria-label={language.t("cowork.enterprise.settings.memberRole")}
                    onChange={(event) => setState("role", event.currentTarget.value as typeof state.role)}
                  >
                    <option value="viewer">{language.t("cowork.members.role.viewer")}</option>
                    <option value="member">{language.t("cowork.enterprise.settings.role.member")}</option>
                    <option value="admin">{language.t("cowork.enterprise.settings.role.admin")}</option>
                  </select>
                  <button
                    type="submit"
                    class="h-9 rounded-md bg-v2-background-bg-contrast px-3 text-[12px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                    disabled={!state.identifier.trim() || state.adding || seatsFull()}
                  >
                    {state.adding
                      ? language.t("cowork.enterprise.settings.inviting")
                      : language.t("cowork.enterprise.settings.invite")}
                  </button>
                </form>
              </SettingsRowV2>
              <Show when={state.invitationNotice}>
                <SettingsRowV2
                  title={language.t("cowork.enterprise.settings.invitations.sent")}
                  description={language.t("cowork.enterprise.settings.invitations.sentDescription", {
                    email: state.invitationNotice,
                  })}
                >
                  <span />
                </SettingsRowV2>
              </Show>
              <Show when={state.error}>
                <SettingsRowV2 title={language.t("cowork.enterprise.settings.error")} description={state.error}>
                  <span />
                </SettingsRowV2>
              </Show>
            </SettingsListV2>
          </section>

          <Show when={(invitations()?.length ?? 0) > 0}>
            <section>
              <h2 class="settings-v2-section-title">{language.t("cowork.enterprise.settings.invitations.pending")}</h2>
              <SettingsListV2>
                <For each={invitations() ?? []}>
                  {(invitation) => (
                    <SettingsRowV2
                      title={invitation.email}
                      description={language.t("cowork.enterprise.settings.invitations.expires", {
                        date: formatDateTime(invitation.expires_at, { dateStyle: "short" }),
                      })}
                    >
                      <div class="flex items-center gap-2">
                        <span class="rounded-md border border-v2-border-border-base px-2 py-1 text-[11px] capitalize text-v2-text-text-muted">
                          {workspaceRoleLabel(invitation.role, language)}
                        </span>
                        <button
                          type="button"
                          class="h-8 rounded-md border border-v2-border-border-base px-3 text-[11px] text-v2-text-text-muted hover:text-v2-text-text-base disabled:opacity-50"
                          disabled={!!state.invitationBusy}
                          onClick={() => void resendInvitation(invitation)}
                        >
                          {language.t("cowork.enterprise.settings.invitations.resend")}
                        </button>
                        <button
                          type="button"
                          class="h-8 rounded-md border border-v2-border-border-base px-3 text-[11px] text-v2-text-text-muted hover:text-v2-text-text-base disabled:opacity-50"
                          disabled={!!state.invitationBusy}
                          onClick={() => void revokeInvitation(invitation)}
                        >
                          {language.t("cowork.enterprise.settings.invitations.revoke")}
                        </button>
                      </div>
                    </SettingsRowV2>
                  )}
                </For>
              </SettingsListV2>
            </section>
          </Show>

          <section>
            <h2 class="settings-v2-section-title">{language.t("cowork.enterprise.settings.currentMembers")}</h2>
            <SettingsListV2>
              <Show
                when={!members.loading && (members()?.length ?? 0) > 0}
                fallback={
                  <SettingsRowV2
                    title={
                      members.loading
                        ? language.t("cowork.enterprise.settings.loadingMembers")
                        : language.t("cowork.enterprise.settings.noMembers")
                    }
                    description={language.t("cowork.enterprise.settings.noMembersDescription")}
                  >
                    <span />
                  </SettingsRowV2>
                }
              >
                <For each={members() ?? []}>
                  {(member) => (
                    <SettingsRowV2
                      title={member.name || member.email || member.user_id}
                      description={
                        [member.username ? `@${member.username}` : "", member.email].filter(Boolean).join(" · ") ||
                        language.t("cowork.enterprise.common.notAvailable")
                      }
                    >
                      <div class="flex items-center gap-2">
                        <span class="rounded-md border border-v2-border-border-base px-2 py-1 text-[11px] capitalize text-v2-text-text-muted">
                          {workspaceRoleLabel(member.role, language)}
                        </span>
                        <span class="text-[11px] text-v2-text-text-muted">
                          {language.t("cowork.enterprise.settings.entityAccess", { count: member.company_ids.length })}
                        </span>
                        <Show when={!isCurrentUser(member)}>
                          <button
                            type="button"
                            class="h-8 rounded-md border border-v2-border-border-base px-3 text-[11px] text-v2-text-text-muted hover:text-v2-text-text-base disabled:opacity-50"
                            disabled={!!state.removing}
                            onClick={() => void remove(member)}
                          >
                            {language.t("cowork.members.remove")}
                          </button>
                        </Show>
                      </div>
                    </SettingsRowV2>
                  )}
                </For>
              </Show>
            </SettingsListV2>
          </section>
        </div>
      </div>
    </div>
  )
}

function workspaceRoleLabel(role: CoworkWorkspaceMember["role"], language: ReturnType<typeof useLanguage>) {
  const keys: Record<CoworkWorkspaceMember["role"], keyof typeof import("@/i18n/en").dict> = {
    viewer: "cowork.members.role.viewer",
    member: "cowork.enterprise.settings.role.member",
    admin: "cowork.enterprise.settings.role.admin",
    owner: "cowork.enterprise.settings.role.owner",
  }
  return language.t(keys[role])
}
